import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { CaretLink } from 'secondstep-components'
import { domains } from 'config/env'
import { pluralizeEnglish, toTitleCase } from 'utils/stringHelpers'
import {
  Bullet,
  ClassCount,
  ContentWrapper,
  DetailsWrapper,
  GradeDataWrapper,
  StyledBodyCopy,
  GradeProgressBarIndicator,
  StyledTooltip,
} from './component.styles'
import {
  GRADES,
  LESSONS_NOT_STARTED_MULTIPLE_CLASSES,
  LESSONS_NOT_STARTED_SINGULAR_CLASS,
  MULTI_CLASSES,
  SINGULAR_CLASS,
} from './constants'
import { getProgressBarVariant } from './helpers'

const NumberOfClasses = ({ classesNotStarted }) => {
  const numOfClassesNotStartedMessage =
    classesNotStarted > 1
      ? LESSONS_NOT_STARTED_MULTIPLE_CLASSES
      : LESSONS_NOT_STARTED_SINGULAR_CLASS
  return (
    <>
      <ClassCount>
        {pluralizeEnglish(classesNotStarted, SINGULAR_CLASS, MULTI_CLASSES)}
      </ClassCount>{' '}
      {numOfClassesNotStartedMessage}
    </>
  )
}

const GradeProgressData = ({
  bhuStatus,
  className,
  classesNotStarted,
  dwbProgressBars,
  grade,
  lessonsCompleted,
  numOfClasses,
  selectedTab,
  siteId,
  totalLessons,
}) => {
  const { viewOption } = useParams()
  const isMounted = useRef(true)
  const percentComplete =
    numOfClasses === 0 ? 0 : Math.round((lessonsCompleted / totalLessons) * 100)
  const shouldRenderIndicator = numOfClasses > 0
  const gradeLabel = GRADES[grade]
  let gradeQuery = grade.includes('dwb') ? grade.replace('dwb-', '') : grade
  const linkUrl = `${domains.LEARN_APP_DOMAIN}/reports/k8/${
    viewOption === 'core' ? 'core_units' : viewOption
  }/?site=${siteId}&grades=${gradeQuery}${bhuStatus ? `&unit=rbhu` : ''}`

  const classCount = pluralizeEnglish(
    numOfClasses,
    toTitleCase(SINGULAR_CLASS),
    toTitleCase(MULTI_CLASSES),
  )
  const caretLinkMessage = `${gradeLabel} • ${classCount}`
  const keyText = bhuStatus
    ? `${siteId}-${grade}`
    : `${siteId}-${grade}-${selectedTab}`
  const lessonsCompletionMessage = `${lessonsCompleted} of ${totalLessons} Lessons`
  const progressBarVariant = getProgressBarVariant({
    bhuStatus,
    dwbProgressBars,
  })

  useEffect(() => {
    return () => (isMounted.current = false)
  }, [])
  return (
    <GradeDataWrapper className={className}>
      <DetailsWrapper>
        <ContentWrapper numOfClasses={numOfClasses}>
          {(classesNotStarted > 0 || lessonsCompleted === 0) && (
            <StyledTooltip
              content={
                <NumberOfClasses classesNotStarted={classesNotStarted} />
              }
            >
              <Bullet />
            </StyledTooltip>
          )}
          <CaretLink dataTestId={grade} href={linkUrl}>
            {caretLinkMessage}
          </CaretLink>
        </ContentWrapper>
        {numOfClasses > 0 && (
          <StyledBodyCopy>{lessonsCompletionMessage}</StyledBodyCopy>
        )}
      </DetailsWrapper>
      <GradeProgressBarIndicator
        $variant={progressBarVariant}
        isParentMounted={isMounted}
        isValueInsideBar
        keyText={keyText}
        percentComplete={percentComplete}
        shouldRenderIndicator={shouldRenderIndicator}
      />
    </GradeDataWrapper>
  )
}

NumberOfClasses.propTypes = {
  classesNotStarted: PropTypes.number,
}

GradeProgressData.propTypes = {
  bhuStatus: PropTypes.bool,
  className: PropTypes.string,
  classesNotStarted: PropTypes.number,
  dwbProgressBars: PropTypes.bool,
  grade: PropTypes.string,
  lessonsCompleted: PropTypes.number,
  numOfClasses: PropTypes.number,
  selectedTab: PropTypes.string,
  siteId: PropTypes.number,
  totalLessons: PropTypes.number,
}

export default GradeProgressData
